/* components/patterns/PaymentsList.jsx — история платежей (унифицирована).
   // заменяет: SubscriptionTab.PaymentsList, BusinessTariffPanel.PaymentsList (фикс №15).
   md+ → таблица; xs/sm → карточки (каждый платёж — отдельная карточка). Пустое состояние.
   Пропсы: payments[], loading. ХЕНДОФФ → frontend/components/common/PaymentsList.jsx. */

import { Pill } from '../ui/Badge.jsx';
import { ErrorState } from './States.jsx';
import { Icon } from '../ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { fmt } from '../../lib/format.js';
import { PLAN_LABELS } from '../../lib/adapters/checkout.js';

  const { useBreakpoint } = hooks;

  const KIND_LABEL = { initial: 'Первый платёж', renewal: 'Продление', upgrade: 'Доплата за апгрейд', refund: 'Возврат' };
  const STATUS = {
    succeeded: { tone: 'ok',      label: 'Оплачен' },
    pending:   { tone: 'info',    label: 'В обработке' },
    failed:    { tone: 'bad',     label: 'Ошибка' },
    // брошенные оформления закрывает свип протухших pending — без этой строки
    // клиент видел в истории оплат сырое латинское «cancelled»
    cancelled: { tone: 'neutral', label: 'Отменён' },
  };

  // Счёт ещё pending, но «В обработке» про него неправда (pending_reason из API):
  // вытесненный счёт на продление висел бы строкой «Продление — В обработке» рядом с
  // новым, и клиент оплатил бы не тот. `hint` — подсказка при наведении (таблица),
  // `note` — подпись под статусом на карточке: касание на телефоне подсказку не покажет.
  const PENDING_REASON = {
    superseded: { tone: 'neutral', label: 'Заменён', note: 'Оплачивать не нужно',
      hint: 'Этот счёт больше не нужно оплачивать: период оплачен другим платежом, выставлен новый счёт или срок подписки изменился.' },
    review: { tone: 'info', label: 'На проверке', note: 'Оплата получена, проверяем',
      hint: 'Оплата по счёту получена, мы проверяем её вручную.' },
  };

  function paymentStatus(p) {
    return (p.status === 'pending' && PENDING_REASON[p.pending_reason])
      || STATUS[p.status] || { tone: 'neutral', label: p.status };
  }

  function StatusPill({ payment }) {
    const s = paymentStatus(payment);
    const pill = <Pill tone={s.tone} size="sm">{s.label}</Pill>;
    return s.hint ? <span title={s.hint}>{pill}</span> : pill;
  }

  function PaymentsList({ payments = [], loading = false, error = null, onRetry }) {
    const { bp } = useBreakpoint();
    const cards = bp === 'xs' || bp === 'sm';

    if (loading) {
      return (
        <div className="pay-list pay-list--loading" aria-busy="true">
          {[0, 1, 2].map((i) => <span key={i} className="skeleton" style={{ height: cards ? 96 : 44, borderRadius: 'var(--radius-md)' }} />)}
        </div>
      );
    }
    // сбой запроса истории — явная ошибка с «Повторить», а не ложное «Платежей пока нет»
    if (error) {
      return <ErrorState title="Не удалось загрузить историю" body="Платежи временно недоступны. Попробуйте ещё раз." onRetry={onRetry} />;
    }
    if (!payments.length) {
      return (
        <div className="pay-empty">
          <Icon name="credit-card" size={24} />
          <span className="t-body text-dim">Платежей пока нет</span>
        </div>
      );
    }

    if (cards) {
      return (
        <div className="pay-cards">
          {payments.map((p) => (
            <div className="pay-card" key={p.id}>
              <div className="pay-card__top">
                <span className="t-label">{KIND_LABEL[p.kind] || p.kind}</span>
                <span className="pay-card__amount num">{fmt.money(p.amount_rub)}</span>
              </div>
              <div className="pay-card__meta">
                <StatusPill payment={p} />
                <span className="t-caption text-dim num">{fmt.date(p.created_at)}</span>
              </div>
              {paymentStatus(p).note && (
                <div className="pay-card__note t-caption text-dim">{paymentStatus(p).note}</div>
              )}
              <div className="pay-card__inv t-caption text-mute num">{p.invoice_id}</div>
              {p.act_pdf_url && (
                <a className="link t-caption" href={p.act_pdf_url} target="_blank" rel="noopener noreferrer">
                  Акт (PDF)
                </a>
              )}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="data-table-wrap">
        <table className="data-table data-table--compact">
          <thead>
            <tr>
              <th>Дата</th><th>Операция</th><th>Тариф</th>
              <th className="is-right">Сумма</th><th>Статус</th><th>Счёт</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id}>
                <td className="num">{fmt.date(p.created_at)}</td>
                <td>{KIND_LABEL[p.kind] || p.kind}</td>
                {/* Название из словаря чеков, если оно там есть; иначе слаг, как раньше
                    (у PLAN_LABELS на промах — «Подписка», здесь она хуже слага). */}
                <td className="cap">{p.plan_slug in PLAN_LABELS ? PLAN_LABELS[p.plan_slug] : p.plan_slug}</td>
                <td className="is-right num">{fmt.money(p.amount_rub)}</td>
                <td><StatusPill payment={p} /></td>
                <td className="num text-mute">
                  {p.invoice_id}
                  {p.act_pdf_url && (
                    <>
                      {' · '}
                      <a className="link" href={p.act_pdf_url} target="_blank" rel="noopener noreferrer">акт</a>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

export { PaymentsList, paymentStatus };
